import { useEffect, useRef } from 'react';
import { InputAdornment, styled, TextField, Tooltip } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import { QuickFilter, QuickFilterClear, QuickFilterControl, QuickFilterTrigger, ToolbarButton } from '@mui/x-data-grid-pro';

const StyledQuickFilterBase = styled(QuickFilter)({
	display: 'grid',
	alignItems: 'center',
	marginLeft: 'auto'
});

interface OwnerState {
	expanded: boolean;
}

const StyledToolbarButton = styled(ToolbarButton)<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
	gridArea: '1 / 1',
	width: 'min-content',
	height: 'min-content',
	zIndex: 1,
	opacity: ownerState.expanded ? 0 : 1,
	pointerEvents: ownerState.expanded ? 'none' : 'auto',
	transition: theme.transitions.create(['opacity'])
}));

const StyledTextField = styled(TextField)<{
	ownerState: OwnerState;
}>(({ theme, ownerState }) => ({
	gridArea: '1 / 1',
	overflowX: 'clip',
	width: ownerState.expanded ? 260 : 'var(--trigger-width)',
	opacity: ownerState.expanded ? 1 : 0,
	transition: theme.transitions.create(['width', 'opacity'])
}));

interface StyledQuickFilterProps {
	autoFocus?: boolean;
	className?: string;
	debounceMs?: number;
	defaultExpanded?: boolean;
	alwaysExpanded?: boolean;
	disabled?: boolean;
	id?: string;
	inputClassName?: string;
	placeholder?: string;
	tooltip?: string;
}

/**
 * MUI Stencil for using the updated Quick Filter in v8
 */
export const StyledQuickFilter = ({ debounceMs = 300, autoFocus, ...props }: StyledQuickFilterProps) => {
	const inputRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		if (autoFocus) {
			// Needs requestAnimationFrame to ensure expansion occurs before focus
			const animationFrame = requestAnimationFrame(() => {
				inputRef.current?.focus();
			});
			return () => {
				cancelAnimationFrame(animationFrame);
			};
		}
	}, [autoFocus]);

	return (
		<StyledQuickFilterBase
			id={props.id}
			className={props.className}
			debounceMs={debounceMs}
			defaultExpanded={props.defaultExpanded}
			expanded
		>
			<QuickFilterTrigger
				render={(triggerProps, state) => (
					<Tooltip
						title='Search'
						enterDelay={0}
					>
						<StyledToolbarButton
							{...triggerProps}
							ownerState={{ expanded: state.expanded }}
							color='default'
							aria-disabled={state.expanded}
						>
							<SearchIcon fontSize='small' />
						</StyledToolbarButton>
					</Tooltip>
				)}
			/>
			<QuickFilterControl
				autoComplete='one-time-code'
				disabled={props.disabled}
				ref={inputRef}
				render={({ ref, ...controlProps }, state) => (
					<StyledTextField
						{...controlProps}
						ownerState={{ expanded: state.expanded }}
						inputRef={ref}
						aria-label='Search'
						placeholder='Search...'
						size='small'
						className={props.inputClassName}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position='start'>
										<SearchIcon fontSize='small' />
									</InputAdornment>
								),
								endAdornment: state.value ? (
									<InputAdornment position='end'>
										<QuickFilterClear
											edge='end'
											size='small'
											aria-label='Clear search'
										>
											<CancelIcon fontSize='small' />
										</QuickFilterClear>
									</InputAdornment>
								) : null,
								...controlProps.slotProps?.input
							},
							...controlProps.slotProps
						}}
					/>
				)}
			/>
		</StyledQuickFilterBase>
	);
};
